import React, { useMemo, useState } from "react";
import { CloudUpload, DeleteOutline } from "@mui/icons-material";
import { Alert, Box, Paper, Typography } from "@mui/material";

import { useFeaturesByLayer } from "fond/hooks/useFeaturesByLayer";
import { useGetImportLayers } from "fond/hooks/useGetImportLayers";
import { addImport } from "fond/redux/imports";
import { featureCollection } from "fond/turf";
import { removeDrawingProperties } from "fond/utils/geojson";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { isImportInProgress } from "fond/utils/layers";
import { ConfirmModal, LinearProgressWithLabel, StackedNavigationButton, SvgIcon, useStackedNavigationContext } from "fond/widgets";
import StackedNavigationHeader from "fond/widgets/StackedNavigation/StackedNavigationHeader";

interface LayerPair {
  controlKey: string;
  outputKey: string;
}

interface SolveOutputControlPanelProps {
  layerPairs: LayerPair[];
  displayName: string;
  editPanelTarget: string;
  uploadPanelTarget?: string;
  canDelete?: boolean;
  onDelete?: () => void | Promise<void>;
}

const SolveOutputControlPanel: React.FC<SolveOutputControlPanelProps> = ({
  layerPairs,
  displayName,
  editPanelTarget,
  uploadPanelTarget,
  canDelete = false,
  onDelete,
}) => {
  const dispatch = useAppDispatch();
  const versionId = useAppSelector((state) => state.project.versionId);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const lowerName = displayName.toLowerCase();

  const outputKeys = useMemo(() => layerPairs.map((p) => p.outputKey), [layerPairs]);
  const controlKeys = useMemo(() => layerPairs.map((p) => p.controlKey), [layerPairs]);
  const { featuresByLayer, isLoading } = useFeaturesByLayer(versionId, outputKeys);

  const controlLayers = useGetImportLayers(versionId, controlKeys);
  const hasAnyControlLayer = layerPairs.some((p) => !!controlLayers.get(p.controlKey)?.layer);
  const hasAnyOutputFeatures = layerPairs.some((p) => (featuresByLayer[p.outputKey]?.length ?? 0) > 0);

  const importStates = useAppSelector((state) => state.imports[versionId]);
  const importing = layerPairs.some((p) => isImportInProgress(importStates?.[p.controlKey]?.status));

  const { open, clear } = useStackedNavigationContext();

  const handleOnDrawButtonClick = () => {
    open(editPanelTarget);
  };

  const handleOnImportButtonClick = async () => {
    setShowConfirmModal(false);
    await Promise.all(
      layerPairs.map(async ({ controlKey, outputKey }) => {
        const features = featuresByLayer[outputKey] ?? [];
        if (features.length === 0) return;
        const file = new File([JSON.stringify(removeDrawingProperties(featureCollection(features)))], "layer.geojson", {
          type: "application/geo+json",
        });
        await dispatch(addImport({ versionId, layerKey: controlKey, displayName, files: [file] }));
      })
    );
  };

  const handleShowConfirmModal = () => {
    setShowConfirmModal(true);
  };

  const handleCancelReplace = () => {
    setShowConfirmModal(false);
  };

  const handleOnUploadButtonClick = () => {
    if (uploadPanelTarget) open(uploadPanelTarget);
  };

  const handleOnDeleteButtonClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!onDelete) return;
    setIsDeleting(true);
    try {
      await onDelete();
      setShowDeleteConfirm(false);
      clear();
    } finally {
      setIsDeleting(false);
    }
  };

  const onBack = () => {
    clear();
  };

  return (
    <Box>
      <StackedNavigationHeader title={displayName} onBackButtonClick={onBack} />
      <ConfirmModal
        open={showConfirmModal}
        header={`Replace existing ${lowerName}?`}
        confirmText="Continue"
        cancelText="Cancel"
        color="error"
        content={
          <Typography variant="body2">
            This will replace all existing {lowerName} data with the solve output. This action cannot be undone.
          </Typography>
        }
        onConfirm={handleOnImportButtonClick}
        onCancel={handleCancelReplace}
      />
      {importing && (
        <>
          <Alert severity="info" sx={{ my: 1 }}>
            FOND is now processing your data, and it will be visible on the map shortly.
          </Alert>
          <Paper variant="outlined" sx={{ py: 2, px: 2.75, mt: 2 }}>
            <LinearProgressWithLabel color="primary" variant="indeterminate" title={`Importing ${lowerName}`} subtext="Processing..." />
          </Paper>
        </>
      )}
      {!importing && (
        <>
          <StackedNavigationButton
            data-testid={`draw-new-${lowerName}-button`}
            icon={<SvgIcon icon="polygon" />}
            title={`Edit ${lowerName}`}
            subtitle={`Edit or draw your own ${lowerName} on the map`}
            onClick={handleOnDrawButtonClick}
          />
          <StackedNavigationButton
            data-testid={`import-${lowerName}-button`}
            icon={<CloudUpload />}
            title={hasAnyControlLayer ? `Replace ${lowerName}` : `Import solve ${lowerName}`}
            subtitle={hasAnyControlLayer ? `Reset ${lowerName} to solve output` : `Import ${lowerName} from solve output`}
            onClick={hasAnyControlLayer ? handleShowConfirmModal : handleOnImportButtonClick}
            disabled={isLoading || !hasAnyOutputFeatures}
          />
          {uploadPanelTarget && (
            <StackedNavigationButton
              data-testid={`upload-${lowerName}-button`}
              icon={<CloudUpload />}
              title="Upload"
              subtitle="Use my data"
              onClick={handleOnUploadButtonClick}
            />
          )}
          {onDelete && (
            <>
              <StackedNavigationButton
                data-testid={`delete-${lowerName}-button`}
                icon={<DeleteOutline />}
                title="Delete"
                subtitle="Remove existing data"
                onClick={handleOnDeleteButtonClick}
                disabled={!canDelete}
              />
              <ConfirmModal
                open={showDeleteConfirm}
                header={`Delete ${lowerName}?`}
                confirmText="Delete"
                cancelText="Cancel"
                color="error"
                content={
                  <Typography variant="body2">This will permanently delete all existing {lowerName} data. This action cannot be undone.</Typography>
                }
                onConfirm={handleConfirmDelete}
                onCancel={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
              />
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default SolveOutputControlPanel;
